// Location validation utilities for DASHBASKET delivery service

// Bujumbura Mairie boundaries (optimized coverage for city core)
// Based on administrative boundaries of Bujumbura Mairie province
// Coverage area: ~9km North-South, ~7km East-West
// Uses strict boundary validation only
export const BUJUMBURA_BOUNDARIES = {
  north: -3.31,     // Northern boundary (9km coverage)
  south: -3.435202,     // Southern boundary 
  east: 29.40,      // Eastern boundary (7km coverage)
  west: 29.31       // Western boundary
};

// Default center coordinates for Bujumbura, Burundi
export const BUJUMBURA_CENTER = {
  lat: -3.3731,
  lng: 29.3739
};

/**
 * Check if coordinates are within Bujumbura Mairie delivery area
 * @param {number} lat - Latitude coordinate
 * @param {number} lng - Longitude coordinate
 * @returns {boolean} - True if within delivery area, false otherwise
 */
export const isWithinDeliveryArea = (lat, lng) => {
  if (!lat || !lng || typeof lat !== 'number' || typeof lng !== 'number') {
    return false;
  }
  
  return (
    lat >= BUJUMBURA_BOUNDARIES.south &&
    lat <= BUJUMBURA_BOUNDARIES.north &&
    lng >= BUJUMBURA_BOUNDARIES.west &&
    lng <= BUJUMBURA_BOUNDARIES.east
  );
};

/**
 * Get formatted address from coordinates using reverse geocoding (if needed)
 * For now, returns a simple coordinate-based description
 * @param {number} lat - Latitude coordinate
 * @param {number} lng - Longitude coordinate
 * @returns {string} - Formatted location description
 */
export const getLocationDescription = (lat, lng) => {
  const isInBoundaries = isWithinDeliveryArea(lat, lng);
  const distance = calculateDistance(lat, lng, BUJUMBURA_CENTER.lat, BUJUMBURA_CENTER.lng);
  
  if (isInBoundaries) {
    return `Bujumbura Mairie (${distance.toFixed(1)}km from center)`;
  } else {
    return `Outside Bujumbura Mairie (${distance.toFixed(1)}km from center)`;
  }
};

/**
 * Calculate distance between two coordinates (in kilometers)
 * Using Haversine formula
 * @param {number} lat1 - First latitude
 * @param {number} lng1 - First longitude
 * @param {number} lat2 - Second latitude
 * @param {number} lng2 - Second longitude
 * @returns {number} - Distance in kilometers
 */
export const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Convert degrees to radians
 * @param {number} degrees - Degrees to convert
 * @returns {number} - Radians
 */
const toRadians = (degrees) => {
  return degrees * (Math.PI / 180);
};

/**
 * Get validation message for location (strict boundary validation)
 * @param {number} lat - Latitude coordinate
 * @param {number} lng - Longitude coordinate
 * @returns {object} - Validation result with isValid and message
 */
export const validateLocation = (lat, lng) => {
  // Strict validation: check only administrative boundaries
  const isValid = isWithinDeliveryArea(lat, lng);
  const distanceFromCenter = calculateDistance(lat, lng, BUJUMBURA_CENTER.lat, BUJUMBURA_CENTER.lng);
  
  let message;
  if (isValid) {
    message = 'Location is within our delivery area';
  } else {
    message = `This location is outside our delivery area. We currently only serve Bujumbura Mairie city limits.`;
  }
  
  return {
    isValid,
    message,
    description: getLocationDescription(lat, lng),
    distanceFromCenter: distanceFromCenter.toFixed(1)
  };
};

/**
 * Check if location is near the city center (for additional validation if needed)
 * @param {number} lat - Latitude coordinate
 * @param {number} lng - Longitude coordinate
 * @param {number} maxDistanceKm - Maximum distance from center in kilometers (default: 10km)
 * @returns {boolean} - True if within distance from center
 */
export const isNearCityCenter = (lat, lng, maxDistanceKm = 10) => {
  const distance = calculateDistance(lat, lng, BUJUMBURA_CENTER.lat, BUJUMBURA_CENTER.lng);
  return distance <= maxDistanceKm;
}; 